'use client';

import { faTimes } from '@fortawesome/pro-light-svg-icons';
import { useEffect, useRef } from 'react';
import { DataTestIdEnum } from '@medprev-web-app/datatestid';

import { ArrivalOrderSlotsGrid } from './_components/arrival-order-slots-grid.component';

import { EntityAvatar } from '@/components/ui/avatar/entity-avatar';
import { Icon } from '@/components/ui/icon.component';
import { Sheet, SheetClose, SheetContent } from '@/components/ui/sheet';
import { Typography } from '@/components/ui/typography/typography.component';
import { TypographyVariant } from '@/components/ui/typography/typography.type';
import { SearchPartnerData } from '@/app/(main)/(pages)/busca/[slug]/_types/search-partner-data.type';
import { EntityTypeEnum } from '@/types/enums/entity-type.enum';
import { useIsGreaterThan } from '@/hooks/use-is-greather-than.hook';
import useDeviceType from '@/hooks/use-device-type.hook';
import { partnerBranchDisplayName } from '@/utils/formatters/branch';
import { professionalDisplayName } from '@/utils/formatters/professional-name';
import { sumProcedurePrices } from '@/utils/procedures/procedures.util';
import { getAnalyticsPlugin } from '@/app/(main)/_components/logger/analytics/get-analytics-plugin';
import { AnalyticsEnum } from '@/types/enums/analytics/analytics.enum';

const MODAL_DAYS_NARROW = 2;
const MODAL_DAYS_WIDE = 3;

interface SearchCardArrivalOrderModalProps {
  scheduleId: string | undefined;
  schedule: SearchPartnerData;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SearchCardArrivalOrderModal({
  scheduleId,
  schedule,
  open,
  onOpenChange,
}: SearchCardArrivalOrderModalProps) {
  const isWide = useIsGreaterThan('sm');
  const limitDays = isWide ? MODAL_DAYS_WIDE : MODAL_DAYS_NARROW;
  const deviceType = useDeviceType();
  const analytics = getAnalyticsPlugin();
  const wasOpen = useRef(open);

  const { professional } = schedule;
  const entityName = professional?.id
    ? professionalDisplayName(professional)
    : partnerBranchDisplayName(schedule.partner);
  const entityAvatar = professional?.id ? professional.avatar : schedule.partner.avatar;
  const entityType = professional?.id ? EntityTypeEnum.PROFESSIONAL : EntityTypeEnum.LABORATORY_COLLECT_POINT;
  const price = sumProcedurePrices(schedule.procedures);

  useEffect(() => {
    if (open && !wasOpen.current) {
      analytics.track(AnalyticsEnum.VIEW_ARRIVAL_ORDER_MODAL, {
        partner_id: schedule.partner.id,
        device_type: deviceType,
      });
    }
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    analytics.track(AnalyticsEnum.CLOSE_ARRIVAL_ORDER_MODAL, { partner_id: schedule.partner.id });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="center-v2"
        className="min-[768px]:h-fit min-[768px]:w-[90vw] min-[768px]:rounded-2xl min-[768px]:max-h-[67vh] min-[768px]:max-w-[640px] md:max-w-[640px] p-0"
        hasHeader
        data-testid={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_MODAL}
      >
        <div className="flex flex-col w-full pt-5">
          <div className="flex items-start gap-2 pl-6 pr-5 pb-4 w-full">
            <Typography variant={TypographyVariant.MODAL_TITLE} className="flex-1">
              Horários disponíveis
            </Typography>
            <SheetClose
              asChild
              className="text-gray-500 cursor-pointer self-start hover:text-gray-900"
              data-testid={DataTestIdEnum.SEARCH_CARD_ARRIVAL_ORDER_MODAL_CLOSE}
              onClick={handleClose}
            >
              <button type="button" aria-label="Fechar" className="size-8 flex items-center justify-center">
                <Icon icon={faTimes} size="xl" />
              </button>
            </SheetClose>
          </div>
        </div>

        <div className="flex flex-col gap-5 px-6 pt-4 pb-5 w-full overflow-y-auto">
          <div className="flex items-start gap-4 w-full">
            <EntityAvatar
              alt={`Foto de perfil de ${entityName}`}
              size="lg"
              src={entityAvatar}
              type={entityType}
              className="rounded-lg"
            />
            <Typography
              variant={TypographyVariant.MEDIUM_SUBTITLE}
              className="flex-1 text-green-500 font-semibold line-clamp-2 leading-6 sm:leading-7"
              as="span"
            >
              {entityName}
            </Typography>
          </div>

          <ArrivalOrderSlotsGrid
            scheduleId={scheduleId}
            limitDays={limitDays}
            partnerId={schedule.partner.id}
            price={price}
            slotClassName="py-2 text-base"
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
